/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * VNU: thanh tiến độ kiểu "Kế hoạch vs Đã thực hiện"
 * (SPEC-vnu-khcn-dashboard, CAP-5, C-13).
 *
 * Thiết kế vẽ hai chuỗi lồng nhau: một thanh nền rộng là chỉ tiêu kế hoạch, một
 * thanh hẹp hơn nằm đè lên là phần đã thực hiện. Trong ECharts đó là hai chuỗi bar
 * cùng vị trí, đạt được bằng `barGap: '-100%'` trên chuỗi thứ hai.
 *
 * Đặt tên tổng quát ("progress overlay") để bất kỳ cặp chỉ tiêu/thực hiện nào cũng
 * dùng được, không riêng dữ liệu VNU — yêu cầu cấu hình được của CAP-9.
 */
import type { SeriesOption } from 'echarts';

/** Bề rộng thanh nền và thanh đè, tính theo phần trăm khoảng dành cho mỗi hạng mục. */
const BACKGROUND_BAR_WIDTH = '60%';
const FOREGROUND_BAR_WIDTH = '34%';

const isBarSeries = (series: SeriesOption): boolean => series.type === 'bar';

/**
 * Chồng chuỗi bar thứ hai lên chuỗi thứ nhất để tạo hình thanh tiến độ.
 *
 * Trả về nguyên mảng đầu vào khi:
 * - tính năng không bật,
 * - có ít hơn hai chuỗi bar (không có gì để chồng),
 * - có nhiều hơn hai chuỗi bar (ý nghĩa "kế hoạch vs thực hiện" không còn rõ, và
 *   chồng bừa sẽ giấu mất dữ liệu — thà hiện đúng kiểu mặc định).
 *
 * Không đụng tới các chuỗi không phải bar (đường xu hướng, chú giải...).
 */
export function applyProgressOverlay(
  series: SeriesOption[],
  enabled?: boolean,
): SeriesOption[] {
  if (!enabled) {
    return series;
  }

  const barIndexes = series.reduce<number[]>((acc, item, index) => {
    if (isBarSeries(item)) {
      acc.push(index);
    }
    return acc;
  }, []);

  if (barIndexes.length !== 2) {
    return series;
  }

  const [backgroundIndex, foregroundIndex] = barIndexes;

  return series.map((item, index) => {
    if (index === backgroundIndex) {
      return {
        ...item,
        barWidth: BACKGROUND_BAR_WIDTH,
        // Thanh nền phải nằm dưới; nếu không, nó che mất phần đã thực hiện.
        z: 2,
      } as SeriesOption;
    }
    if (index === foregroundIndex) {
      return {
        ...item,
        barWidth: FOREGROUND_BAR_WIDTH,
        // '-100%' kéo chuỗi này về đúng vị trí chuỗi trước đó thay vì đứng cạnh.
        barGap: '-100%',
        z: 3,
      } as SeriesOption;
    }
    return item;
  });
}
